import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Zap } from "lucide-react";

const BANNERS = [
  {
    title: "Groceries in 10 minutes",
    subtitle: "Fresh daily. Delivered faster than you can say ‘order’.",
    cta: "Shop Groceries",
    link: "Groceries",
    bg: "from-emerald-500 to-teal-500",
    img: "https://images.pexels.com/photos/36574790/pexels-photo-36574790.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tag: "FLAT ₹50 OFF",
  },
  {
    title: "Hot food, hotter deals",
    subtitle: "Pizza, burger, biryani — up to 60% off from top kitchens.",
    cta: "Order Food",
    link: "Food",
    bg: "from-rose-500 to-orange-500",
    img: "https://images.pexels.com/photos/19036022/pexels-photo-19036022.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tag: "MEGA SALE",
  },
  {
    title: "Medicines at your door",
    subtitle: "Prescription + OTC — delivered in minutes, 24×7.",
    cta: "Shop Medicine",
    link: "Medicine",
    bg: "from-sky-500 to-indigo-500",
    img: "https://images.unsplash.com/photo-1766186616111-ee448e1b6aa5?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    tag: "20% OFF",
  },
  {
    title: "Household essentials",
    subtitle: "Detergents, cleaning, personal care — stock up and save.",
    cta: "Shop Essentials",
    link: "Essentials",
    bg: "from-purple-500 to-fuchsia-500",
    img: "https://images.unsplash.com/photo-1583947215259-38e31be8751f?w=940&q=80",
    tag: "BUY 1 GET 1",
  },
];

export default function OfferCarousel({ onBannerClick }) {
  const [idx, setIdx] = useState(0);
  const trackRef = useRef(null);
  const paused = useRef(false);

  useEffect(() => {
    const t = setInterval(() => {
      if (paused.current) return;
      setIdx((i) => (i + 1) % BANNERS.length);
    }, 3000);
    return () => clearInterval(t);
  }, []);

  const go = (n) => setIdx((n + BANNERS.length) % BANNERS.length);

  return (
    <section
      className="relative"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      data-testid="offer-carousel"
    >
      <div className="relative rounded-2xl overflow-hidden" ref={trackRef}>
        <div
          className="flex transition-transform duration-500 ease-[cubic-bezier(.21,.9,.29,1)]"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {BANNERS.map((b) => (
            <button
              key={b.title}
              onClick={() => onBannerClick?.(b.link)}
              className={`min-w-full h-44 sm:h-52 lg:h-60 relative text-left bg-gradient-to-r ${b.bg} text-white px-6 sm:px-10 flex items-center`}
              data-testid={`banner-${b.link}`}
            >
              <div className="flex-1 relative z-10 max-w-md">
                <div className="inline-flex items-center gap-1 text-[11px] font-bold tracking-widest bg-white/20 border border-white/30 rounded-full px-2 py-0.5">
                  <Zap size={11} /> {b.tag}
                </div>
                <h3 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">{b.title}</h3>
                <p className="text-white/85 text-sm sm:text-base mt-1 max-w-sm">{b.subtitle}</p>
                <span className="inline-flex mt-4 bg-white text-slate-900 rounded-lg px-4 py-2 text-sm font-bold shadow">
                  {b.cta} →
                </span>
              </div>
              <img
                src={b.img}
                alt=""
                className="absolute right-0 top-0 h-full w-[55%] object-cover opacity-80 mix-blend-overlay pointer-events-none"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent pointer-events-none" />
            </button>
          ))}
        </div>
      </div>

      {/* Controls */}
      <button
        onClick={() => go(idx - 1)}
        className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow items-center justify-center hover:bg-white"
        aria-label="Previous"
      >
        <ChevronLeft size={16} className="text-slate-900" />
      </button>
      <button
        onClick={() => go(idx + 1)}
        className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 shadow items-center justify-center hover:bg-white"
        aria-label="Next"
      >
        <ChevronRight size={16} className="text-slate-900" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {BANNERS.map((b, i) => (
          <button
            key={`dot-${b.title}`}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-white" : "w-1.5 bg-white/50"}`}
            aria-label={`Go to banner ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
